import React, {
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Editor from '../../Editor';
import FormatBubbleMenu from '../../../menus/FormatBubbleMenu';
import FixedMobileMenu from '../../../menus/FixedMobileMenu/FixedMobileMenu';
import { extConfig } from './config';
import type { CommentEditorProps } from './types';
import { useEditorContext } from '../../Context';

import viewerStyles from '../../../Viewer/presets/Comment/Comment.scss';
import s from './Comment.scss';

function isChildOfAnyElement(
  containerElementRefs: (RefObject<HTMLDivElement | null> | undefined)[],
  target?: Element | null
) {
  if (!target) {
    return false;
  }

  let found = false;
  containerElementRefs.forEach(containerElementRef => {
    if (found || !containerElementRef?.current) {
      return;
    }

    if (
      // If we don't ignore root element focus, this can lead to some issues
      // when our usual implementations collapse their controls and have to dump the user's
      // focus somewhere.
      !containerElementRef.current.isEqualNode(target) &&
      containerElementRef.current.contains(target)
    ) {
      found = true;
      return;
    }
  });
  return found;
}

const CommentMenus = ({ rootElementRef }) => {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [shouldShowMobileMenu, setShouldShowMobileMenu] = useState(false);
  const isMobile = useContext(MobileContext);
  const editor = useEditorContext();

  // Since the mobile toolbar is in a portal, we need to track global focusin and focusout events to determine when it
  // should be shown.
  // We take popper as being a child, this might not be 100% accurate but should be 99%...
  useEffect(() => {
    if (isMobile && document) {
      const containerElementRefs = [rootElementRef, toolbarRef];

      const checkAndSetMenuVisibility = ev => {
        const should =
          isChildOfAnyElement(
            containerElementRefs as any,
            ev.target as Element
          ) || (ev.target as any)?.closest('.editor-sub-menu') !== null;
        setShouldShowMobileMenu(should);
        if (should && shouldShowMobileMenu !== should) {
          editor?.commands.focus();
        }
      };

      const focusCallback = (ev: FocusEvent) => {
        checkAndSetMenuVisibility(ev);
      };

      const checkOutsideClick = ev => {
        checkAndSetMenuVisibility(ev);
      };

      document.addEventListener('focusin', focusCallback);
      document.addEventListener('touchstart', checkOutsideClick);
      return () => {
        document.removeEventListener('focusin', focusCallback);
        document.removeEventListener('touchstart', checkOutsideClick);
      };
    }
  }, [rootElementRef, isMobile, editor?.commands, shouldShowMobileMenu]);

  return isMobile && shouldShowMobileMenu ? (
    <FixedMobileMenu
      noAtomics
      ref={toolbarRef}
      className={cn(s['mobile-toolbar'], shouldShowMobileMenu && s['visible'])}
    />
  ) : (
    <FormatBubbleMenu omitHRule />
  );
};

const CommentEditor: React.FC<CommentEditorProps> = ({
  rootElementRef,
  className,
  ...rest
}) => {
  const Menu = useCallback(() => {
    return <CommentMenus rootElementRef={rootElementRef} />;
  }, [rootElementRef]);

  return (
    <Editor
      {...rest}
      presetId="comment"
      className={cn(s['root'], viewerStyles['root'], className)}
      extensionConfig={extConfig}
    >
      {Menu}
    </Editor>
  );
};
CommentEditor.displayName = 'CommentEditor';

export default CommentEditor;
